import { motion } from "framer-motion";

export default function DownloadSection({ onGetApp }) {
  return (
    <section
      id="download"
      data-testid="download-section"
      className="py-24 lg:py-40 bg-[#0C0E13] relative overflow-hidden"
    >
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#3B9EFF]/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#3B9EFF]/30 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#3B9EFF]/4 rounded-full blur-[120px]" />
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: "linear-gradient(#3B9EFF 1px, transparent 1px), linear-gradient(90deg, #3B9EFF 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
        {/* Label */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-3 mb-8"
        >
          <div className="w-8 h-px bg-[#3B9EFF]" />
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#3B9EFF] font-outfit">Get the App</span>
          <div className="w-8 h-px bg-[#3B9EFF]" />
        </motion.div>

        {/* Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-outfit font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter uppercase text-white leading-[0.93] mb-8"
        >
          Pre-Register<br />
          <span className="text-[#3B9EFF]">Now</span><br />
          on Fanible
        </motion.h2>

        {/* Subtext */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-base md:text-lg text-[#A1A1AA] max-w-xl mx-auto mb-4 leading-relaxed font-inter"
        >
          Pre-registration is now open on Google Play Store. Community and Articles arrive at launch — more features drop soon after. Be among the first fans on the platform.
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.25 }}
          className="flex items-center justify-center gap-3 mb-10"
        >
          <span className="flex items-center gap-1.5 text-xs text-[#3B9EFF] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-[#3B9EFF]" /> Community ✓
          </span>
          <span className="text-white/15">·</span>
          <span className="flex items-center gap-1.5 text-xs text-[#3B9EFF] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-[#3B9EFF]" /> Articles ✓
          </span>
          <span className="text-white/15">·</span>
          <span className="flex items-center gap-1.5 text-xs text-[#A1A1AA]">
            <span className="w-1.5 h-1.5 rounded-full bg-white/20" /> Shop · Coming Soon
          </span>
          <span className="text-white/15">·</span>
          <span className="flex items-center gap-1.5 text-xs text-[#A1A1AA]">
            <span className="w-1.5 h-1.5 rounded-full bg-white/20" /> Shoutout · Coming Soon
          </span>
        </motion.div>

        {/* Download Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
        >
          <button
            data-testid="download-playstore-btn"
            onClick={onGetApp}
            className="flex items-center gap-4 bg-[#3B9EFF] text-black font-bold rounded-full px-8 py-5 hover:bg-white transition-colors duration-150 hover:scale-[1.04] active:scale-[0.97] group"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 flex-shrink-0">
              <path d="M3 20.5v-17c0-.83 1.01-1.3 1.63-.74L20 11.5 4.63 21.24C4.01 21.8 3 21.33 3 20.5z" />
            </svg>
            <div className="text-left">
              <div className="text-[11px] font-medium opacity-60 uppercase tracking-wider leading-none mb-1">Pre-Register on</div>
              <div className="text-base font-black uppercase tracking-wide leading-none">Google Play</div>
            </div>
          </button>

          <a
            href="#"
            data-testid="download-appstore-btn"
            className="flex items-center gap-4 bg-transparent border-2 border-white/30 text-white font-bold rounded-full px-8 py-5 hover:border-[#3B9EFF]/60 hover:text-[#3B9EFF] transition-all duration-150 hover:scale-[1.04] active:scale-[0.97]"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 flex-shrink-0">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            <div className="text-left">
              <div className="text-[11px] font-medium opacity-60 uppercase tracking-wider leading-none mb-1">Coming soon to</div>
              <div className="text-base font-black uppercase tracking-wide leading-none">App Store</div>
            </div>
          </a>
        </motion.div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="flex flex-wrap justify-center gap-6 text-[#A1A1AA] text-xs"
        >
          {["Free to Pre-Register", "Pre-Registration Now Open", "Android First · iOS Later", "Community & Articles at Launch"].map((badge) => (
            <div key={badge} className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-[#3B9EFF]" />
              {badge}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
